import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Card } from "@/components/portal/ui";
import { ConfirmPayment } from "@/components/admin/ManualPayment";

const pct = (s) => (s == null ? "—" : `${Math.round(s * 100)}%`);
const date = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const H = ({ children }) => <h2 className="font-display text-xl text-[#1A1918] mt-8 mb-3">{children}</h2>;

function TuitionControl({ sid, course, current, onSaved, testid }) {
  const [status, setStatus] = useState(current || "unpaid");
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await http.put(`/admin/academics/students/${sid}/tuition`, { course, status, reason }); toast.success("Tuition status recorded"); setReason(""); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`${testid}-select`} className="border border-[#E5E1D8] rounded-full px-3 py-1.5 text-sm bg-white">
        <option value="unpaid">Unpaid</option><option value="paid">Paid</option><option value="complimentary">Complimentary</option>
      </select>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Note (e.g. payment reference or waiver reason)" data-testid={`${testid}-reason`} className="border border-[#E5E1D8] rounded-full px-3 py-1.5 text-sm w-64" />
      <button onClick={save} data-testid={`${testid}-save`} className="text-sm rounded-full bg-[#1A1918] text-white px-4 py-1.5">Record</button>
    </div>
  );
}

function Payments({ rows }) {
  if (!rows.length) return null;
  return <details className="text-xs text-[#8A998C] mt-1"><summary className="cursor-pointer">Manual payments ({rows.length})</summary>{rows.map((p, i) => <div key={i}>{p.received_on} · ${p.amount.toFixed(2)} via {p.method} · recorded by {p.recorded_by}{p.note ? ` · ${p.note}` : ""}</div>)}</details>;
}

function Enrollments({ d, reload }) {
  const missing = ["is-101", "ns-101", "is-201", "is-301", "is-401"].filter((s) => !d.enrollments.some((e) => e.course === s));
  const pay = (course, code, manual, testid) => <ConfirmPayment sid={d.student.id} student={d.student.name || d.student.email} course={course} code={code} prices={d.prices[course]} manual={manual} onSaved={reload} testid={testid} />;
  return (
    <>
      <H>Enrollment & Tuition</H>
      <Card className="p-0 overflow-x-auto" data-testid="profile-enrollments">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4]">{["Course", "Class", "Tuition", "Status", "Record manual status"].map((h) => <th key={h} className="p-4 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {d.enrollments.length === 0 && <tr><td colSpan={5} className="p-4 text-[#6B665E]">Not Enrolled — no tuition owed.</td></tr>}
            {d.enrollments.map((e) => (
              <tr key={e.id} className="border-b border-[#F0ECE4] last:border-0 align-top" data-testid={`enrollment-${e.course}`}>
                <td className="p-4"><div className="font-medium">{e.code} {e.title}</div><div className="text-xs text-[#8A998C]">Since {date(e.created_at)} · {e.source?.replace("_", " ")}</div></td>
                <td className="p-4">{e.cohort}<div className="text-xs text-[#8A998C]" data-testid={`format-${e.course}`}>{e.format}{e.faculty?.status ? ` · faculty-led ${e.faculty.status}` : ""}</div></td>
                <td className="p-4">{e.tuition}</td>
                <td className="p-4"><span className="text-xs rounded-full px-2.5 py-1 bg-[#EEF2EE] text-[#56705B]" data-testid={`tuition-status-${e.course}`}>{e.tuition_label}</span>
                  {e.reason && <div className="text-xs text-[#8A998C] mt-1">{e.reason}</div>}
                  {e.history.length > 0 && <details className="text-xs text-[#8A998C] mt-1"><summary className="cursor-pointer">Audit history ({e.history.length})</summary>{e.history.map((h, i) => <div key={i}>{date(h.at)} · {h.status} · {h.by}{h.reason ? ` · ${h.reason}` : ""}</div>)}</details>}
                  <Payments rows={d.manual_payments.filter((p) => p.course === e.course)} /></td>
                <td className="p-4">{e.stripe ? <span className="text-xs text-[#8A998C]" data-testid={`tuition-stripe-${e.course}`}>Paid via Stripe — manage refunds in Stripe</span>
                  : e.paid_via === "manual" ? <span className="text-xs text-[#8A998C]" data-testid={`tuition-manual-${e.course}`}>Paid in full — manual payment record kept</span>
                  : <div className="space-y-2">{e.payment_status === "unpaid" && pay(e.course, e.code, e.manual, `confirm-payment-${e.course}`)}{!e.manual?.received && <TuitionControl sid={d.student.id} course={e.course} current={e.payment_status} onSaved={reload} testid={`tuition-control-${e.course}`} />}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {missing.map((s) => <Card key={s} className="p-4 mt-3" data-testid={`record-${s.replace("-", "")}`}><div className="text-sm text-[#6B665E] mb-2">Record a {s.toUpperCase()} self-paced enrollment manually (e.g. tuition received outside online checkout, or a waiver). This records status only and creates no Stripe transaction or revenue.</div><div className="flex flex-wrap items-center gap-3">{pay(s, s.toUpperCase(), null, `confirm-payment-new-${s.replace("-", "")}`)}<TuitionControl sid={d.student.id} course={s} current="unpaid" onSaved={reload} testid={`tuition-control-new-${s.replace("-", "")}`} /></div></Card>)}
      <p className="text-xs text-[#8A998C] mt-2">Manual status never creates Stripe charges, purchases or revenue. Complimentary is not collected tuition. Live-course enrollments are created under Classes & Cohorts.</p>
    </>
  );
}

function LessonTable({ rows, prefix }) {
  return (
    <Card className="p-0 overflow-x-auto" data-testid={`${prefix}-coursework`}>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4]">{["Lesson", "Status", "Best quiz score", "Attempts", "Applied coursework"].map((h) => <th key={h} className="p-4 font-medium">{h}</th>)}</tr></thead>
        <tbody>{rows.map((l) => (
          <tr key={l.key} className="border-b border-[#F0ECE4] last:border-0 align-top" data-testid={`${prefix}-lesson-${l.number}`}>
            <td className="p-4">{l.number}. {l.title}</td><td className="p-4">{l.status}</td><td className="p-4">{pct(l.best_score)}</td><td className="p-4">{l.attempts}</td>
            <td className="p-4 text-[#6B665E] max-w-sm">{l.coursework ? <details><summary className="cursor-pointer">Submitted</summary><p className="whitespace-pre-wrap mt-1">{l.coursework}</p></details> : "—"}</td>
          </tr>))}</tbody>
      </table>
    </Card>
  );
}

function FinalSummary({ f, testid, slug }) {
  const { id } = useParams();
  const label = f.completed_at ? "Completed" : f.state === "pending" ? "Pending Faculty Review" : f.state === "released" ? (f.passed ? "Final released · Passed (modules remaining)" : "Final released · Additional work required") : f.state === "draft" ? "Draft in progress" : "Not submitted";
  return (
    <Card className="p-4 mt-3" data-testid={testid}>
      <div className="text-xs text-[#8A998C] mb-1">Final course assessment · faculty reviewed</div>
      <p className="text-sm">{f.state === "pending" ? <Link to={`/admin/students?tab=final&open=${slug}-${id}`} className="text-xs rounded-full px-2.5 py-1 mr-2 bg-[#F3EFE9] text-[#9F634E] underline hover:bg-[#EADFD3]" data-testid={`${testid}-status`}>{label} — Review now →</Link> : <span className={`text-xs rounded-full px-2.5 py-1 mr-2 ${f.completed_at ? "bg-[#172A3A] text-[#F5F2E9]" : "bg-[#F3EFE9] text-[#9F634E]"}`} data-testid={`${testid}-status`}>{label}</span>}
        {f.state === "released" && ` ${f.grade}/${f.max} · released ${date(f.released_at)}`}{f.completed_at ? ` · Course completed ${date(f.completed_at)}` : ""}{f.state === "pending" ? ` Submitted ${date(f.submitted_at)} · attempt ${f.attempt}` : ""}</p>
      {f.remarks && <p className="text-sm text-[#6B665E] mt-2 whitespace-pre-wrap">Instructor remarks: {f.remarks}</p>}
      {f.history.length > 0 && <p className="text-xs text-[#8A998C] mt-1">Earlier released attempts: {f.history.map((h) => `${h.grade}/${h.max} ${h.passed ? "passed" : "not passed"}`).join(" · ")}</p>}
    </Card>
  );
}

function NSCoursework({ d }) {
  return d.self_paced.filter((s) => s.enrolled || s.lessons.some((l) => l.attempts || l.coursework)).map((s) => {
    const done = s.lessons.filter((l) => l.status === "Completed").length;
    const tid = s.slug.replace("-", "");
    return (
      <div key={s.slug}>
        <H>{s.code} Coursework · {done} of {s.lessons.length} lessons completed</H>
        <LessonTable rows={s.lessons} prefix={`profile-${tid}`} />
        <FinalSummary f={d.finals[s.slug]} slug={s.slug} testid={`profile-${tid}-final`} />
      </div>
    );
  });
}

function Coursework({ d }) {
  const done = d.is101_lessons.filter((l) => l.status === "Completed").length;
  return (
    <>
      <H>IS-101 Coursework · {done} of {d.is101_lessons.length} lessons completed</H>
      <LessonTable rows={d.is101_lessons} prefix="profile" />
      {d.orientation && <Card className="p-4 mt-3"><div className="text-xs text-[#8A998C] mb-1">Orientation exercise</div><p className="text-sm whitespace-pre-wrap">{d.orientation}</p></Card>}
      <FinalSummary f={d.finals["is-101"]} slug="is-101" testid="profile-final" />
    </>
  );
}

function Observations({ logs }) {
  return (
    <>
      <H>Sleep Observation Tracker · {logs.length} entries</H>
      <Card className="p-0 overflow-x-auto" data-testid="profile-observations">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4]">{["Date", "Bedtime", "Wakings", "Longest stretch", "Morning wake", "Naps", "Notes"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={7} className="p-4 text-[#6B665E]">No observations recorded.</td></tr>}
            {logs.map((l) => <tr key={l.id} className="border-b border-[#F0ECE4] last:border-0"><td className="p-3">{l.date}</td><td className="p-3">{l.bedtime || "—"}</td><td className="p-3">{l.wakings ?? "—"}</td><td className="p-3">{l.longest_stretch || "—"}</td><td className="p-3">{l.morning_wake || "—"}</td><td className="p-3">{l.naps ?? "—"}</td><td className="p-3 text-[#6B665E]">{l.comments || ""}</td></tr>)}
          </tbody>
        </table>
      </Card>
    </>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const load = () => http.get(`/admin/academics/students/${id}`).then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (d === null) return <div className="text-[#6B665E]">Loading…</div>;
  if (!d) return <div className="text-[#6B665E]">Could not load this student record.</div>;
  const s = d.student;
  return (
    <div data-testid="student-academic-profile">
      <Link to="/admin/students" data-testid="profile-back" className="text-sm text-[#9F634E] hover:underline">← Student Records</Link>
      <div className="eyebrow text-[#8A998C] mt-4 mb-1">Education Center — Student Academic Profile</div>
      <h1 className="font-display text-3xl text-[#1A1918]" data-testid="profile-name">{s.name || s.email}</h1>
      <p className="text-[#6B665E] text-sm mt-1">{s.email} · Registered {date(s.created_at)}{s.child_age ? ` · Child age: ${s.child_age}` : ""}</p>
      <div className="flex flex-wrap gap-2 mt-3 text-xs">
        <span className="rounded-full px-2.5 py-1 bg-[#EEF2EE] text-[#56705B]" data-testid="profile-academic-status">{d.academic_status}</span>
        {d.interest_courses.length > 0 && <span className="rounded-full px-2.5 py-1 bg-[#F3EFE9] text-[#9F634E]">Interest list (not an enrollment): {d.interest_courses.join(", ")}</span>}
        {d.has_consulting_record && <Link to={`/operator/customer/${s.id}`} data-testid="profile-consulting-link" className="rounded-full px-2.5 py-1 border border-[#E5E1D8] text-[#6B665E] hover:border-[#9F634E]">Private consulting record (separate) →</Link>}
      </div>
      <Enrollments d={d} reload={load} />
      <Coursework d={d} />
      <NSCoursework d={d} />
      <Observations logs={d.sleep_logs} />
      <H>Messages · {d.messages.length}</H>
      <Card className="p-4 space-y-2" data-testid="profile-messages">
        {d.messages.length === 0 ? <p className="text-sm text-[#6B665E]">No messages.</p> : d.messages.map((m) => <div key={m.id} className="text-sm"><span className="text-xs text-[#8A998C]">{date(m.created_at)} · {m.sender === "customer" ? "Student" : "Instructor"}:</span> {m.text || m.body}</div>)}
      </Card>
    </div>
  );
}
